import React, { useRef, useState } from 'react';
import { FiPaperclip, FiTrash2, FiUpload } from 'react-icons/fi';
import {
  uploadStatusAttachment,
  downloadStatusAttachment,
  deleteStatusAttachment,
} from '../services/api';

const MAX_BYTES = 10 * 1024 * 1024;

const ACCEPT =
  '.pdf,.csv,.xls,.xlsx,.doc,.docx,.txt,.rtf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.tif,.tiff,.zip,.rar,.7z,.kml,.kmz,.geojson,.json,.xml,.html,.htm,.ppt,.pptx,.odt,.ods';

function formatSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusDetailAttachments({
  blockId,
  statusType,
  attachments = [],
  hasSavedDetail,
  onAttachmentsChange,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handlePickFile = () => {
    if (!hasSavedDetail || disabled || uploading) return;
    inputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError(null);
    if (file.size > MAX_BYTES) {
      setError('Each file must be 10 MB or smaller.');
      return;
    }

    setUploading(true);
    try {
      await uploadStatusAttachment(blockId, statusType, file);
      if (onAttachmentsChange) {
        await onAttachmentsChange();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (att) => {
    try {
      await downloadStatusAttachment(att.id, att.original_filename);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Download failed');
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Remove "${att.original_filename}"?`)) return;
    setError(null);
    try {
      await deleteStatusAttachment(att.id);
      if (onAttachmentsChange) {
        await onAttachmentsChange();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Delete failed');
    }
  };

  return (
    <div className="form-group status-attachments-group">
      <label>Attachments</label>
      {!hasSavedDetail ? (
        <p className="attachments-hint">Save this stage first, then you can upload files (max 10 MB each).</p>
      ) : (
        <>
          <div className="attachments-toolbar">
            <button
              type="button"
              className="upload-files-button"
              onClick={handlePickFile}
              disabled={disabled || uploading}
            >
              <FiUpload aria-hidden />
              {uploading ? 'Uploading...' : 'Upload files'}
            </button>
            <span className="attachments-limit">PDF, CSV, Excel, Word, text, images, and more — 10 MB max per file</span>
          </div>
          <input
            ref={inputRef}
            type="file"
            className="attachments-file-input"
            accept={ACCEPT}
            onChange={handleFileChange}
            disabled={disabled || uploading}
            tabIndex={-1}
          />
        </>
      )}

      {attachments.length > 0 && (
        <ul className="attachments-list">
          {attachments.map((att) => (
            <li key={att.id} className="attachment-item">
              <button
                type="button"
                className="attachment-download-link"
                onClick={() => handleDownload(att)}
                title="Download"
              >
                <FiPaperclip aria-hidden />
                <span>{att.original_filename}</span>
                <span className="attachment-size">({formatSize(att.size_bytes)})</span>
              </button>
              {hasSavedDetail && !disabled && (
                <button
                  type="button"
                  className="attachment-delete-button"
                  onClick={() => handleDelete(att)}
                  title="Remove file"
                  aria-label={`Remove ${att.original_filename}`}
                >
                  <FiTrash2 aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && <p className="attachments-error">{error}</p>}
    </div>
  );
}

/** Read-only clickable filenames for status details tables. */
export function AttachmentFileList({ attachments }) {
  if (!attachments?.length) return null;

  const handleDownload = async (att) => {
    try {
      await downloadStatusAttachment(att.id, att.original_filename);
    } catch (err) {
      console.error('Download failed:', err);
      window.alert(err.response?.data?.error || err.message || 'Download failed');
    }
  };

  return (
    <div className="detail-row attachments-row">
      <span className="detail-label">Files:</span>
      <div className="detail-value attachment-links">
        {attachments.map((att) => (
          <button
            key={att.id}
            type="button"
            className="attachment-name-link"
            onClick={() => handleDownload(att)}
            title="Download"
          >
            {att.original_filename}
          </button>
        ))}
      </div>
    </div>
  );
}

export default StatusDetailAttachments;
